import { useEffect, useRef, useState, useCallback } from "react";
import { trackFrontendEvent } from "../../utils/tracking";

// Bars are not presentation proof. Rendered requires the native canvas data
// update and a container that intersects the viewport.
export const useChartDeliveryReceipt = ({
  enabled, threadUuid, messageId, sourceMessageId, tickerId, scale,
  destination, moduleId = null, loaded = false, unavailable = false,
}) => {
  const key = JSON.stringify([threadUuid, messageId, tickerId, scale, destination, moduleId]);
  const containerRef = useRef(null);
  const [paintedKey, setPaintedKey] = useState(null);
  const [visibleKey, setVisibleKey] = useState(null);
  const sentRef = useRef({ key: null, states: new Set(), started: 0 });
  const onRendered = useCallback((painted) => {
    if (painted) setPaintedKey(key);
  }, [key]);
  const painted = paintedKey === key;
  const status = unavailable ? "unavailable" :
    painted && visibleKey === key && loaded ? "rendered" : loaded ? "loaded" : "loading";

  useEffect(() => {
    if (!enabled || !containerRef.current || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      setVisibleKey(entry.isIntersecting ? key : null);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [enabled, key]);

  useEffect(() => {
    if (!enabled) return;
    if (sentRef.current.key !== key) {
      sentRef.current = { key, states: new Set(), started: performance.now() };
    }
    const receipt = sentRef.current;
    for (const next of ["assigned", status]) {
      if (receipt.states.has(next)) continue;
      receipt.states.add(next);
      trackFrontendEvent("workspace_response_chart_delivery", {
        thread_uuid: threadUuid, response_message_id: messageId,
        source_message_id: sourceMessageId, ticker_id: tickerId, scale,
        destination, module_id: moduleId, status: next,
        elapsed_ms: Math.round(performance.now() - receipt.started),
      });
    }
  }, [destination, enabled, key, messageId, moduleId, scale, sourceMessageId, status, threadUuid, tickerId]);

  return { containerRef, painted, onRendered, status: enabled ? status : undefined };
};
